import { initializeApp } from 'firebase/app';

import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
} from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { getAccessToken, isLoginAsSession } from './authData';
import {
  assertAuthStateMatchesRefreshedAccessToken,
  clearAuthAndRedirectAfterRefreshFailure,
  clearAuthAndRedirectToSignIn,
  getAuthSessionIdentity,
  getLatestAccessTokenAfterAuthChange,
  hasAuthSessionIdentityChanged,
  refreshAccessToken,
  resolveLoginAsRequestAuthFailure,
} from './authRefresh';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
export const storage = getStorage(app);

// Check if using emulators
const isUsingEmulators = process.env.REACT_APP_ENV === 'Development' && process.env.REACT_APP_USE_EMULATORS === 'true';

// Connect to emulators BEFORE setting up auth listeners
if (isUsingEmulators) {
  try {
    connectFirestoreEmulator(db, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', 8080);
  } catch {
    // Firestore emulator connection failed
  }

  try {
    connectStorageEmulator(storage, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', 9199);
  } catch {
    // Storage emulator connection failed
  }

  try {
    connectAuthEmulator(auth, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', { disableWarnings: true });
  } catch {
    // Auth emulator connection failed
  }
}

// Token refresh function
export const refreshFirebaseToken = async () => {
  try {
    let authToken = getAccessToken();
    if (!authToken) {
      console.warn('[Firebase] No authorization token — user may be logged out');
      return false;
    }

    const wasLoginAsRequest = isLoginAsSession();
    const authSessionIdentity = getAuthSessionIdentity(authToken);
    let response = await fetch(`${process.env.REACT_APP_API_URL}auth/firebase-token`, {
      headers: {
        authorization: authToken,
      },
    });

    if (response.status === 401) {
      if (wasLoginAsRequest) {
        resolveLoginAsRequestAuthFailure();
        return false;
      }

      if (hasAuthSessionIdentityChanged(authSessionIdentity)) {
        return false;
      }

      const latestAccessToken = getLatestAccessTokenAfterAuthChange(authToken);
      if (latestAccessToken) {
        authToken = latestAccessToken;
      } else {
        try {
          authToken = await refreshAccessToken();
          assertAuthStateMatchesRefreshedAccessToken(authToken);
        } catch (error) {
          clearAuthAndRedirectAfterRefreshFailure(error);
          return false;
        }
      }
      response = await fetch(`${process.env.REACT_APP_API_URL}auth/firebase-token`, {
        headers: {
          authorization: authToken,
        },
      });

      if (response.status === 401) {
        if (hasAuthSessionIdentityChanged(authSessionIdentity)) {
          return false;
        }

        clearAuthAndRedirectToSignIn();
        return false;
      }
    }

    if (!response.ok) {
      console.error(`[Firebase] Token refresh failed: HTTP ${response.status}`);
      return false;
    }

    const { token } = await response.json();
    if (!token) {
      console.error('[Firebase] Token refresh returned empty token');
      return false;
    }

    await signInWithCustomToken(auth, token);
    return true;
  } catch (error) {
    console.error('[Firebase] Token refresh error:', error);
    return false;
  }
};

// Auto-refresh token when it's about to expire
let tokenCheckInterval: NodeJS.Timeout | null = null;

onAuthStateChanged(auth, async (user) => {
  // Clear existing interval
  if (tokenCheckInterval) {
    clearInterval(tokenCheckInterval);
    tokenCheckInterval = null;
  }

  if (user) {
    // Skip token refresh checks when using emulators
    if (isUsingEmulators) {
      return;
    }

    // Check token expiration every minute (production only)
    tokenCheckInterval = setInterval(async () => {
      try {
        const token = await user.getIdToken();
        const decoded = JSON.parse(atob(token.split('.')[1]));
        const expiresIn = decoded.exp * 1000 - Date.now();

        if (expiresIn < 5 * 60 * 1000) {
          await refreshFirebaseToken();
        }
      } catch {
        await refreshFirebaseToken();
      }
    }, 60 * 1000);
  } else {
    // Firebase auth is null — IndexedDB restoration may have failed.
    // If the user has backend auth, restore Firebase auth proactively.
    const authToken = getAccessToken();
    if (authToken) {
      await refreshFirebaseToken();
    }
  }
});
